// Recurring sale inquiries - the enquiries a Tiedustelu campaign collects.
//
// Pure: no Firebase, no React. The documents in customers/{cid}/recurringinquiries are
// written by the backend, so this file never assumes an exact field name. Every getter
// tries the spellings a writer plausibly used and falls back to '' rather than showing
// "undefined" in a grid cell, and the details dialog renders whatever the document
// actually holds so nothing is hidden by a guess being wrong.

import moment from 'moment'

export const INQUIRY_STATUSES = [
  { value: 'new', label: 'Uusi' },
  { value: 'responded', label: 'Vastattu' },
  { value: 'done', label: 'Valmis' },
]

export const DEFAULT_STATUS = 'new'

export const statusLabel = (value) => {
  const found = INQUIRY_STATUSES.find((s) => s.value === value)
  return found ? found.label : value || statusLabel(DEFAULT_STATUS)
}

// Colours the MUI Chip. 'new' is the one that wants attention.
export const statusColor = (value) => {
  if (value === 'done') return 'default'
  if (value === 'responded') return 'primary'
  return 'warning'
}

export const isKnownStatus = (value) => INQUIRY_STATUSES.some((s) => s.value === value)

/* Reading a document written elsewhere */

const firstNonEmpty = (doc, keys) => {
  if (!doc) return ''
  for (let i = 0; i < keys.length; i += 1) {
    const value = doc[keys[i]]
    if (value !== undefined && value !== null && String(value).trim() !== '') return value
  }
  return ''
}

// Timestamps arrive as Firestore Timestamps, ISO strings, or epoch numbers depending on
// who wrote them. Anything unreadable becomes '' rather than the Unix epoch.
export const toDisplayDate = (value, format) => {
  if (!value) return ''
  const fmt = format || 'DD.MM.YYYY HH:mm'
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') return moment(value.toDate()).format(fmt)
    if (typeof value.seconds === 'number') return moment.unix(value.seconds).format(fmt)
    if (value instanceof Date) return moment(value).format(fmt)
    return ''
  }
  if (typeof value === 'number') return moment(value > 1e11 ? value : value * 1000).format(fmt)
  const parsed = moment(String(value), moment.ISO_8601, true)
  if (parsed.isValid()) return parsed.format(fmt)
  const loose = moment(String(value), ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD', 'DD.MM.YYYY HH:mm', 'DD.MM.YYYY'])
  return loose.isValid() ? loose.format(fmt) : ''
}

// Sortable key for a timestamp of any shape, so the grid orders correctly even when the
// displayed string is localised.
export const toSortKey = (value) => {
  if (!value) return 0
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') return value.toDate().getTime()
    if (typeof value.seconds === 'number') return value.seconds * 1000
    if (value instanceof Date) return value.getTime()
    return 0
  }
  if (typeof value === 'number') return value > 1e11 ? value : value * 1000
  const m = moment(String(value))
  return m.isValid() ? m.valueOf() : 0
}

/* The requested slot */

// The occurrence timestamps inside slot.dates are 'YYYY-MM-DD HH:mm' - space separated
// and without the Z that slot.start/end carry.
const DATE_FORMATS = ['YYYY-MM-DD HH:mm', moment.ISO_8601]

// ISO weekday, matching slot.weekday and the WEEKDAYS list in ActivityCreator.
export const WEEKDAYS = ['', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su']

export const slotOf = (doc) => (doc && doc.slot && typeof doc.slot === 'object' ? doc.slot : null)

// What the customer is actually asking for: a weekly slot. Confirmed against real data -
// { start, end, weekday, duration, resourceId, displayName }, start/end ISO Zulu.
//
// Read with moment.utc, NOT local time. Reservations and activities in this codebase store
// times as UTC wall clock and are displayed as-is; converting '2027-04-19T13:00:00Z' to
// Finnish local time would show 16:00 for a slot everyone calls 13:00.
// Duration as hours with the minutes alongside: 150 -> '2,5 h (150 min)'.
//
// Two decimals then trailing zeros stripped, so a quarter-hour slot stays exact - one
// decimal would render 75 min as '1,3 h', and slot lengths are routinely 75 or 105.
export const formatDuration = (minutes) => {
  const total = Number(minutes)
  if (!isFinite(total) || total <= 0) return ''
  const hours = (total / 60).toFixed(2).replace(/\.?0+$/, '').replace('.', ',')
  return hours + ' h (' + total + ' min)'
}

export const slotSummary = (slot) => {
  if (!slot) return ''
  const start = slot.start ? moment.utc(slot.start) : null
  const end = slot.end ? moment.utc(slot.end) : null
  // Derived from isoWeekday rather than moment's .format('dd'), which depends on whichever
  // module last set moment's global locale - English elsewhere would render 'Mo', not 'Ma'.
  const day = WEEKDAYS[Number(slot.weekday)] || (start && start.isValid() ? WEEKDAYS[start.isoWeekday()] : '')
  const times =
    start && start.isValid() && end && end.isValid() ? start.format('HH:mm') + '–' + end.format('HH:mm') : ''
  return [day, times, formatDuration(slot.duration)].filter(Boolean).join(' ')
}

// Sortable position in the week, so Monday 08:00 orders before Monday 13:00 before
// Tuesday 09:00. Sorting the summary string instead would put 'Ke' before 'Ma'.
export const slotWeekPosition = (slot) => {
  if (!slot) return 0
  const start = slot.start ? moment.utc(slot.start) : null
  const valid = start && start.isValid()
  const minutes = valid ? start.hours() * 60 + start.minutes() : 0
  const weekday = Number(slot.weekday) || (valid ? start.isoWeekday() : 0)
  return weekday * 1440 + minutes
}

// The individual occurrences: [{ start, end, available }], 'YYYY-MM-DD HH:mm'. One entry
// per week of the season, 35 of them in the observed documents.
export const slotDates = (slot) => (slot && Array.isArray(slot.dates) ? slot.dates : [])

// When the season actually runs, taken from the occurrence list.
//
// Deliberately NOT from slot.start: in the observed data slot.start is 2027-04-19 while
// the season opens 2026-08-31 - it is occurrence 34 of 35, not the first. Anything that
// called slot.start the season start was simply wrong.
export const seasonSpan = (slot) => {
  const dates = slotDates(slot)
  if (dates.length === 0) return { first: '', last: '', count: 0, unavailable: 0, firstsort: 0 }
  const first = moment.utc(dates[0].start, DATE_FORMATS)
  const lastEntry = dates[dates.length - 1]
  const last = moment.utc(lastEntry.end || lastEntry.start, DATE_FORMATS)
  return {
    first: first.isValid() ? first.format('DD.MM.YYYY') : '',
    last: last.isValid() ? last.format('DD.MM.YYYY') : '',
    firstsort: first.isValid() ? first.valueOf() : 0,
    count: dates.length,
    // An occurrence the venue cannot actually offer. All 35 were available in the
    // observed data, but the flag exists, so whoever answers the inquiry needs to see it.
    unavailable: dates.filter((d) => d && d.available === false).length,
  }
}

// The first date the requested slot falls on - the season's opening occurrence.
export const slotFirstDate = (slot) => seasonSpan(slot).first

// How the occurrences looked WHEN THE ENQUIRY WAS MADE. The available flags are a snapshot
// the backend wrote at that moment, not live availability - a slot free then may be taken
// by now, which is why the UI labels this "Kyselyhetken tilanne".
export const availabilityLabel = (unavailable) => {
  const count = Number(unavailable) || 0
  return count > 0 ? count + ' ei vapaana' : 'kaikki vapaana'
}

// Every occurrence, for the admin answering the inquiry: date, time, and whether the venue
// can actually offer it. Kept in the order the backend wrote them, which is chronological.
export const occurrenceRows = (slot) =>
  slotDates(slot).map((entry, index) => {
    const start = moment.utc(entry.start, DATE_FORMATS)
    const end = moment.utc(entry.end, DATE_FORMATS)
    // Weekday from isoWeekday and the local list, not moment's .format('dd'), for the same
    // reason as slotSummary: that depends on whichever module last set moment's global
    // locale, so it renders 'ma' here only because something else loaded moment/locale/fi.
    const day = start.isValid() ? WEEKDAYS[start.isoWeekday()] : ''
    return {
      key: index,
      date: start.isValid() ? day + ' ' + start.format('DD.MM.YYYY') : String(entry.start || ''),
      time:
        start.isValid() && end.isValid() ? start.format('HH:mm') + '–' + end.format('HH:mm') : '',
      // Only an explicit false means unavailable; a missing flag is not a refusal.
      available: entry.available !== false,
    }
  })

// What the whole season costs. A stored total wins; failing that, price per occurrence
// times the number of occurrences. Returns '' rather than 0 when either input is missing,
// so the column stays blank instead of claiming the season is free.
export const seasonTotal = (doc, price, occurrences) => {
  const stored = firstNonEmpty(doc, ['fullprice', 'totalprice', 'total', 'seasonprice'])
  if (stored !== '') return Number(stored)
  // Where it actually lives: slot.fullprice, 2380 against price 68 x 35 occurrences.
  const slot = slotOf(doc)
  const onSlot = slot ? firstNonEmpty(slot, ['fullprice', 'totalprice', 'total']) : ''
  if (onSlot !== '') return Number(onSlot)
  const unit = Number(price)
  const times = Number(occurrences)
  if (!isFinite(unit) || !isFinite(times) || price === '' || occurrences === '' || times <= 0) return ''
  return Math.round(unit * times * 100) / 100
}

// One grid row per document. Unknown-shaped fields are read through aliases; `raw` keeps
// the whole document so the details dialog can show everything.
export const normaliseInquiry = (doc) => {
  const status = isKnownStatus(doc && doc.status) ? doc.status : DEFAULT_STATUS
  const created = firstNonEmpty(doc, ['created', 'timestamp', 'createdat', 'created_at', 'date', 'sent'])
  const changedAt = firstNonEmpty(doc, ['statuschangedat'])
  const slot = slotOf(doc)
  // Deliberately not `docPrice || slotPrice`: a legitimate price of 0 is falsy and would
  // fall through to the slot, or to '' when there is no slot.
  const docPrice = firstNonEmpty(doc, ['price', 'cost', 'amount'])
  const price = docPrice !== '' ? docPrice : slot ? firstNonEmpty(slot, ['price', 'cost']) : ''
  const span = seasonSpan(slot)
  // dates.length agrees with occurrences (35 = 35) in the observed data; the array wins if
  // they ever disagree, since it is the thing actually being booked.
  const occurrences = span.count || firstNonEmpty(doc, ['occurrences', 'occurrencies', 'count', 'times'])
  return {
    id: doc.id,
    status,
    name: firstNonEmpty(doc, ['name', 'displayName', 'customername', 'contactname', 'fullname']),
    email: firstNonEmpty(doc, ['email', 'useremail', 'customeremail', 'contactemail']),
    phone: firstNonEmpty(doc, ['phone', 'phonenumber', 'tel', 'puhelin']),
    message: firstNonEmpty(doc, ['message', 'note', 'notes', 'comment', 'text', 'description', 'viesti']),
    campaign: firstNonEmpty(doc, ['campaignname', 'recurringsalename', 'campaign', 'recurringsaleid', 'saleid']),
    // The court lives on the slot; the top-level aliases are the fallback.
    resource:
      (slot && (slot.displayName || slot.resourceName)) ||
      firstNonEmpty(doc, ['resourcename', 'resource', 'court']) ||
      (slot && slot.resourceId) ||
      '',
    slot,
    slotsummary: slotSummary(slot),
    slotfirstdate: slotFirstDate(slot),
    slotsort: slotWeekPosition(slot),
    // EUROS. 85 for a 150-minute weekly slot only makes sense as euros, and both
    // activity.price and reservation cost are euros in this codebase. Read from the
    // document first, then from the slot, so it works wherever the backend puts it.
    price,
    // Weekly repeats across the season. Confirmed in real data as `occurrences`, and as
    // the length of slot.dates.
    occurrences,
    seasonfirst: span.first,
    seasonlast: span.last,
    seasonsort: span.firstsort,
    unavailable: span.unavailable,
    // Season total. Prefer a stored value if the backend ever writes one, otherwise
    // derive it: `price` is per occurrence, verified against real documents where a 2 h
    // slot is 68 and a 2,5 h slot is 85 for the same 35-week season.
    fullprice: seasonTotal(doc, price, occurrences),
    created,
    createdsort: toSortKey(created),
    changedby: firstNonEmpty(doc, ['statuschangedbyemail', 'statuschangedby']),
    changedat: changedAt,
    changedatsort: toSortKey(changedAt),
    raw: doc,
  }
}

/* Status changes */

// The patch written when an admin moves an inquiry along. `at` is passed in rather than
// read from the clock so this stays pure and testable.
//
// Firestore forbids a sentinel like serverTimestamp() nested inside arrayUnion, so the log
// entry carries a client-side timestamp while the top-level fields can use the server one.
// The caller adds that sentinel and the arrayUnion wrapper.
export const buildStatusUpdate = (status, user, at) => ({
  status,
  statuschangedat: at,
  statuschangedbyid: (user && user.uid) || (user && user.id) || '',
  statuschangedbyemail: (user && user.email) || '',
  logentry: {
    status,
    at,
    byid: (user && user.uid) || (user && user.id) || '',
    byemail: (user && user.email) || '',
  },
})

// The audit trail, newest first. Tolerates the field being absent on documents written
// before status tracking, and entries written under either key spelling.
export const statusHistory = (doc) => {
  const log = (doc && (doc.statuslog || doc.statushistory)) || []
  if (!Array.isArray(log)) return []
  return log
    .map((entry) => ({
      status: entry.status || '',
      by: entry.byemail || entry.by || entry.byid || '',
      at: entry.at || entry.timestamp || '',
      atsort: toSortKey(entry.at || entry.timestamp || ''),
    }))
    .sort((a, b) => b.atsort - a.atsort)
}

// Fields to show in the details dialog: everything the document holds, minus the plumbing
// the dialog renders separately or that means nothing to a reader.
const HIDDEN_FIELDS = ['id', 'statuslog', 'statushistory', 'raw']

export const detailRows = (doc) => {
  if (!doc) return []
  return Object.keys(doc)
    .filter((key) => !HIDDEN_FIELDS.includes(key))
    .sort()
    .map((key) => {
      const value = doc[key]
      let display
      if (value === null || value === undefined) display = ''
      else if (typeof value === 'object') {
        // A Firestore Timestamp reads as a date, a slot as its weekly summary; anything
        // else falls back to JSON so a nested object is at least inspectable.
        const asDate = toDisplayDate(value)
        if (asDate) display = asDate
        else if (value.start && value.end) {
          display = [slotSummary(value), slotFirstDate(value), value.displayName].filter(Boolean).join(', ')
        } else display = JSON.stringify(value)
      } else if (typeof value === 'boolean') display = value ? 'kyllä' : 'ei'
      else display = String(value)
      return { key, display }
    })
}
